import * as React from "react";
import { createTheme, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { enUS } from "date-fns/locale";
import useTheme from "../../../../common/hooks/useTheme";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

type Props = {
  label?: string;
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  error?: boolean;
  helperText?: string;
};

const CustomDatePicker = (props: Props) => {
  const theme = useTheme();

  // Create theme to match TextInputField styling exactly
  const customTheme = createTheme({
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            width: "100%",
            "& .MuiOutlinedInput-root": {
              height: "56px",
              borderRadius: "8px",
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
              fontFamily: "var(--font-Medium)",
              fontSize: "12px",
              lineHeight: "20px",
              // Lock the container padding to prevent icon movement
              padding: "0 12px",
              // Input text positioning to match TextInputField
              "& input": {
                padding: "24px 12px 10px 0px",
                fontFamily: "var(--font-Medium)",
                fontSize: "12px",
                lineHeight: "20px",
              },
              // Keep calendar icon absolutely positioned
              "& .MuiInputAdornment-root": {
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                margin: 0,
              },
              "& fieldset": {
                borderColor: props.error
                  ? theme.colors_ds.DANGER_GRAPH_DEFICIT
                  : theme.colors_ds.NEUTRAL_BORDER_WEAK,
                top: 0,
              },
              "&:hover fieldset": {
                borderColor: props.error
                  ? theme.colors_ds.DANGER_GRAPH_DEFICIT
                  : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
              },
              "&.Mui-focused fieldset": {
                borderColor: props.error
                  ? theme.colors_ds.DANGER_GRAPH_DEFICIT
                  : theme.colors_ds.PRIMARY_ICON_MAIN,
                borderWidth: "1px",
              },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "var(--font-Medium)",
              fontSize: "10px",
              backgroundColor: "transparent",
              transition: "transform 0.2s ease-in-out",
              color: props.error
                ? theme.colors_ds.DANGER_GRAPH_DEFICIT
                : theme.colors_ds.NEUTRAL_TEXT_WEAK,
              // Position label like TextInputField - larger when empty, centered
              transform: "translate(12px, 16px) scale(1.5)",
              "&.Mui-focused": {
                color: props.error
                  ? theme.colors_ds.DANGER_GRAPH_DEFICIT
                  : theme.colors_ds.PRIMARY_ICON_MAIN,
                transform: "translate(12px, 6px) scale(1)",
              },
              // Position label when shrunk (has value) - smaller, at top
              "&.MuiInputLabel-shrink": {
                transform: "translate(12px, 6px) scale(1)",
              },
            },
            "& .MuiFormHelperText-root": {
              fontFamily: "var(--font-Medium)",
              fontSize: "10px",
              color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
              marginLeft: "12px",
              marginTop: "4px",
            },
            "& legend": {
              display: "none",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            boxShadow: "var(--box-shadow-general)",
            marginTop: "8px",
            border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
          },
        },
      },
      // @ts-ignore
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
          },
          label: {
            fontFamily: "var(--font-Bold)",
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          spacer: {
            width: "2px",
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&[aria-colindex='6']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-colindex='7']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-selected='true']": {
              color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
              fontFamily: "var(--font-Bold)",
            },
          },
          today: {
            borderColor: "transparent",
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontFamily: "var(--font-Bold)",
            "&[aria-label='Saturday']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
            "&[aria-label='Sunday']": {
              color: theme.colors_ds.PRIMARY_TEXT_MAIN,
            },
          },
          weekContainer: {
            fontFamily: "var(--font-Bold)",
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&.Mui-selected": {
              fontFamily: "var(--font-Bold)",
              fontSize: "12px",
              borderRadius: "8px",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
            "&:hover": {
              borderRadius: "8px",
            },
          },
        },
      },
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            "&.Mui-selected": {
              fontFamily: "var(--font-Bold)",
              fontSize: "12px",
              borderRadius: "8px",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
            },
            "&:hover": {
              borderRadius: "8px",
            },
          },
        },
      },
    },
  });

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={{ ...enUS, options: { weekStartsOn: 1 } }}
    >
      <ThemeProvider theme={customTheme}>
        <DatePicker
          label={props.label}
          value={props.value}
          onChange={props.onChange}
          minDate={props.minDate}
          maxDate={props.maxDate}
          views={["year", "month", "day"]}
          openTo="day"
          format="MMM dd yyyy"
          slots={{
            previousIconButton: KeyboardArrowLeftRoundedIcon,
            nextIconButton: KeyboardArrowRightRoundedIcon,
            switchViewIcon: KeyboardArrowDownRoundedIcon,
          }}
          slotProps={{
            textField: {
              placeholder: props.placeholder || "MMM DD YYYY",
              error: props.error,
              helperText: props.helperText,
              inputProps: {
                readOnly: true,
              },
            },
            switchViewIcon: {
              sx: {
                width: "24px",
                height: "24px",
                fontSize: "2rem",
                padding: 0,
                cursor: "pointer",
                borderRadius: "50%",
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
              },
            },
            previousIconButton: {
              sx: {
                width: "32px",
                height: "32px",
                cursor: "pointer",
                padding: "4px",
                borderRadius: "50%",
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                "&:hover": {
                  backgroundColor: theme.colorTokens.N[30],
                },
              },
            },
            nextIconButton: {
              sx: {
                width: "32px",
                height: "32px",
                cursor: "pointer",
                padding: "4px",
                borderRadius: "50%",
                color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
                "&:hover": {
                  backgroundColor: theme.colorTokens.N[30],
                },
              },
            },
          }}
        />
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
